import { useState, useEffect, useCallback, useRef } from 'react';
import { useApiFetch } from '../api';
import { ApiResponseVmPowerStatus } from '../types/api/ApiResponseVirtualMachine';

const POLL_INTERVAL = 10000;

interface UseVmPowerStatusReturn {
  powerState: string | null;
  isLoading: boolean;
  error: string | null;
  refetchPowerStatus: () => void;
}

export const useVmPowerStatus = (vmId: number | null): UseVmPowerStatusReturn => {
  const [powerState, setPowerState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const apiFetch = useApiFetch();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchPowerStatus = useCallback(async () => {
    if (!vmId) return;

    try {
      const response = await apiFetch(`/datacenters/vms/${vmId}/power`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseVmPowerStatus;
      if (data.status === 'success') {
        setPowerState(data.data.state);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to fetch power status');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch power status');
    } finally {
      setIsLoading(false);
    }
  }, [vmId, apiFetch]);

  useEffect(() => {
    if (!vmId) return;

    fetchPowerStatus();

    intervalRef.current = setInterval(fetchPowerStatus, POLL_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [vmId, fetchPowerStatus]);

  return { powerState, isLoading, error, refetchPowerStatus: fetchPowerStatus };
};
